import { distanceBetweenPoints } from '../measurement-utils';
import { getAverageTouchDistanceFromTouchCenter, getCenterPointOfTouchEvent } from '../touch-event-utils';
import { InputRecognizer } from './input-recognizer';
var TouchState;
(function (TouchState) {
    TouchState[TouchState["None"] = 0] = "None";
    TouchState[TouchState["Tap"] = 1] = "Tap";
    TouchState[TouchState["DoubleTap"] = 2] = "DoubleTap";
    TouchState[TouchState["PinchPan"] = 3] = "PinchPan";
})(TouchState || (TouchState = {}));
export class TouchRecognizer extends InputRecognizer {
    constructor(singleTapDelayThreshold, singleTapPositionThreshold) {
        super();
        this.state = TouchState.None;
        this.singleTapDelayElapsed = false;
        this.touchStartEvents = [];
        this.touchMoveEvents = [];
        this.touchEndEvents = [];
        this.onSingleTap = (onSingleTapHandler) => (this.onSingleTapHandler = onSingleTapHandler);
        this.onDoubleTap = (onDoubleTapHandler) => (this.onDoubleTapHandler = onDoubleTapHandler);
        this.onPinch = (onPinchHandler) => (this.onPinchHandler = onPinchHandler);
        this.start = (event) => {
            event.preventDefault();
            if (event.touches.length === 1) {
                if (this.state === TouchState.None) {
                    this.state = TouchState.Tap;
                    this.handleFirstTap();
                }
                else if (this.state === TouchState.Tap) {
                    if (!this.touchWasWithinSingleTapPositionThreshold(event)) {
                        this.resolveTap();
                        this.start(event);
                        return;
                    }
                    this.state = TouchState.DoubleTap;
                    this.clearFirstTap();
                }
            }
            else {
                this.clearFirstTap();
            }
            this.touchStartEvents.push(event);
        };
        this.move = (event) => {
            event.preventDefault();
            if (this.state === TouchState.Tap && !this.touchWasWithinSingleTapPositionThreshold(event)) {
                this.clearFirstTap();
            }
            const panned = super.handlePan(event);
            if (panned) {
                this.state = TouchState.PinchPan;
            }
            if (event.touches.length > 1) {
                this.handlePinch(event);
                this.state = TouchState.PinchPan;
            }
            this.touchMoveEvents.push(event);
        };
        this.end = (event) => {
            event.preventDefault();
            this.touchEndEvents.push(event);
            if (this.state === TouchState.Tap || this.state === TouchState.DoubleTap) {
                this.tryResolveFirstTap();
            }
            else {
                this.reset();
            }
        };
        this.singleTapDelayThreshold = singleTapDelayThreshold || 250;
        this.singleTapPositionThreshold = singleTapPositionThreshold || 10;
    }
    reset() {
        super.reset();
        clearTimeout(this.singleTapDelayTimer);
        this.singleTapDelayElapsed = false;
        this.touchStartEvents = [];
        this.touchEndEvents = [];
        this.touchMoveEvents = [];
        this.state = TouchState.None;
        this.initialPinchDistance = undefined;
    }
    handlePinch(event) {
        const pinchDistance = getAverageTouchDistanceFromTouchCenter(event);
        if (this.initialPinchDistance === undefined) {
            this.initialPinchDistance = pinchDistance;
        }
        else if (this.onPinchHandler) {
            this.onPinchHandler(pinchDistance / this.initialPinchDistance, getCenterPointOfTouchEvent(event));
        }
    }
    handleFirstTap() {
        this.singleTapDelayTimer = setTimeout(() => {
            this.singleTapDelayElapsed = true;
            if (this.touchStartEvents.length === 1 && this.touchEndEvents.length === 1) {
                this.tryResolveFirstTap();
            }
            else {
                // Could potentially emit a long press event here in the future
                this.reset();
            }
        }, this.singleTapDelayThreshold);
    }
    clearFirstTap() {
        clearTimeout(this.singleTapDelayTimer);
    }
    tryResolveFirstTap() {
        if (this.state === TouchState.Tap) {
            if (this.singleTapDelayElapsed)
                this.resolveTap();
        }
        else if (this.state === TouchState.DoubleTap) {
            this.resolveTap();
        }
    }
    resolveTap() {
        const firstTouchStart = this.touchStartEvents[0];
        const touchPoint = {
            x: firstTouchStart.touches[0].pageX,
            y: firstTouchStart.touches[0].pageY
        };
        if (this.state === TouchState.Tap && this.onSingleTapHandler) {
            this.onSingleTapHandler(touchPoint, firstTouchStart.target || undefined);
        }
        else if (this.state === TouchState.DoubleTap && this.onDoubleTapHandler) {
            this.onDoubleTapHandler(touchPoint, firstTouchStart.target || undefined);
        }
        this.reset();
    }
    touchWasWithinSingleTapPositionThreshold(event) {
        const firstTapPoint = getCenterPointOfTouchEvent(this.touchStartEvents[0]);
        const currentTapPoint = getCenterPointOfTouchEvent(event);
        return distanceBetweenPoints(firstTapPoint, currentTapPoint) <= this.singleTapPositionThreshold;
    }
}
